import { AdmissionData } from '../../../interfaces/AdmissionData';
import { AdmissionDataRules } from './types/fieldRuleTypes';
import { FieldRule, UpdatedAdmissionRulesDetail } from './interfaces/fieldRuleInterfaces';
import { Events } from '../../../constants/Events';
import initialRules from './InitialRules';
import clone from 'clone';
import { WorkCardTypes } from './enums/WorkCardType';
import { cpfNormalizers, removeNonNumericValues } from './normalizers';
import AdmissionMetadata from '../../../types/AdmissionMetadata';
import { ProfessionalIdentificationTypes } from '../../../constants/ProfessionalIdentificationTypes';
import { PaymentMethods } from '../../../constants/PaymentMethod';

/**
 * remove field from form, hiding it and making it disabled
 * @param field
 */
const removeField = (field: FieldRule | undefined): void => {
  if (!field)
    field = {};
  field.hidden = true;
  field.disabled = true;
  field.required = false;
};

const enableField = (field: FieldRule | undefined, makeRequired = false): void => {
  if (!field)
    field = {};
  field.hidden = false;
  field.disabled = false;
  field.required = makeRequired;
};

/**
 * Hide field but stills sends it to backend
 */
const hideField = (field: FieldRule | undefined, makeRequired = false): void => {
  if (!field)
    field = {};
  field.hidden = true;
  field.disabled = false;
  field.required = makeRequired;
};

const disableField = (field: FieldRule | undefined, makeRequired = false): void => {
  if (!field)
    field = {};
  field.hidden = false;
  field.disabled = true;
  field.required = makeRequired;
};

const updateRules = (
  formData: AdmissionData,
  admissionMetadata: AdmissionMetadata,
): UpdatedAdmissionRulesDetail => {
  const newRules: AdmissionDataRules = clone(initialRules); // Deep copy
  const updatedFormData: AdmissionData = clone(formData); // Deep copy


  if (updatedFormData.personal.birthPlace?.country) {
    //105 = Brasil
    if (updatedFormData.personal.birthPlace?.country === '105') {
      enableField(newRules.personal?.birthPlace?.state, true);

      if (updatedFormData.personal.birthPlace?.state) {
        enableField(newRules.personal.birthPlace?.city, true);
      } else {
        disableField(newRules.personal.birthPlace?.city, true);
        updatedFormData.personal.birthPlace.city = undefined;
      }
    }

    //Exterior
    if (updatedFormData.personal.birthPlace?.country !== '105') {
      hideField(newRules.personal?.birthPlace?.state, true);
      hideField(newRules.personal?.birthPlace?.city, true);
      updatedFormData.personal.birthPlace.state = 'EX';
      updatedFormData.personal.birthPlace.city = 9000;
    } else {
      //Clear the field if it was previously an foreigner country
      if (updatedFormData.personal.birthPlace.state === 'EX') {
        updatedFormData.personal.birthPlace.state = undefined;
      }

      //Foreigner town code on the app
      if (updatedFormData.personal.birthPlace.city === 9000) {
        updatedFormData.personal.birthPlace.city = undefined;
      }
    }
  } else {
    //clears and disables dependent fields
    disableField(newRules.personal?.birthPlace?.state, true);
    disableField(newRules.personal?.birthPlace?.city, true);

    if (updatedFormData.personal.birthPlace?.state) {
      updatedFormData.personal.birthPlace.state = undefined;
    }

    if (updatedFormData.personal.birthPlace?.city) {
      updatedFormData.personal.birthPlace.city = undefined;
    }
  }


  // Habilitar campo cidade somente depois que o estado for selecionado (step Address)
  if (formData.address.state !== undefined) {
    enableField(newRules.address.city, true);
  }

  // When field has values, make other fields from group required
  if (updatedFormData.personalDocuments?.voterRegistrationNumber) {
    enableField(newRules.personalDocuments?.voterRegistrationZone, true);
    enableField(newRules.personalDocuments?.voterRegistrationSection, true);
    enableField(newRules.personalDocuments?.voterRegistrationState, true);
  } else {
    //cleans group field data if data is not set, default rules from InitialRules.ts
    disableField(newRules.personalDocuments?.voterRegistrationZone, false);
    disableField(newRules.personalDocuments?.voterRegistrationSection, false);
    disableField(newRules.personalDocuments?.voterRegistrationState, false);
    updatedFormData.personalDocuments.voterRegistrationZone = undefined;
    updatedFormData.personalDocuments.voterRegistrationSection = undefined;
    updatedFormData.personalDocuments.voterRegistrationState = undefined;
  }


  if (updatedFormData.personalDocuments?.foreigner !== undefined) {
    if (updatedFormData.personalDocuments?.foreigner === true) {
      // - Se, colaborador selecionar entrangeiro o sistema deve:
      // bloquear os campos referentes ao RG.
      // O número do RG deve ser preenchido com o número do RNE.
      // E a UF do RG com "Exterior".
      // - A nova plataforma deve apresentar o campo ESTRANGEIRO habilitado e selecionado se a UF do RG vier preenchida com "Exterior" do 4Bee Folha.
      //update rules
      disableField(newRules.personalDocuments.idCardIssuingState, true);
      disableField(newRules.personalDocuments.idCard);
      disableField(newRules.personalDocuments.idCardIssuanceDate);
      disableField(newRules.personalDocuments.idCardIssuingAuthority);
      enableField(newRules.personalDocuments.rneCard, true);

      if (updatedFormData.personalDocuments.rneCard) {
        enableField(newRules.personalDocuments.rneCardIssuingAuthority, true);
        enableField(newRules.personalDocuments.rneCardIssuanceDate, false);
        enableField(newRules.personalDocuments.foreignerCountryArrivalDate, false);
        enableField(newRules.personalDocuments.foreignerRneCountry, false);
        enableField(newRules.personalDocuments.foreignerNationalityCode, false);
        enableField(newRules.personalDocuments.foreignerEntryCondition, true);
        enableField(newRules.personalDocuments.foreignerIsMarriedWithBrazilian, true);
        enableField(newRules.personalDocuments.foreignerHasChildrenWithBrazilian, true);
      } else {
        disableField(newRules.personalDocuments.rneCardIssuingAuthority, true);
        disableField(newRules.personalDocuments.rneCardIssuanceDate, false);
        disableField(newRules.personalDocuments.foreignerCountryArrivalDate, false);
        disableField(newRules.personalDocuments.foreignerRneCountry, false);
        disableField(newRules.personalDocuments.foreignerNationalityCode, false);
        disableField(newRules.personalDocuments.foreignerEntryCondition, true);
        disableField(newRules.personalDocuments.foreignerIsMarriedWithBrazilian, true);
        disableField(newRules.personalDocuments.foreignerHasChildrenWithBrazilian, true);

        updatedFormData.personalDocuments.rneCardIssuingAuthority = undefined;
        updatedFormData.personalDocuments.rneCardIssuanceDate = undefined;
        updatedFormData.personalDocuments.foreignerCountryArrivalDate = undefined;
        updatedFormData.personalDocuments.foreignerRneCountry = undefined;
        updatedFormData.personalDocuments.foreignerNationalityCode = undefined;
        updatedFormData.personalDocuments.foreignerEntryCondition = undefined;
        updatedFormData.personalDocuments.foreignerIsMarriedWithBrazilian = undefined;
        updatedFormData.personalDocuments.foreignerHasChildrenWithBrazilian = undefined;
      }

      //update form Data
      updatedFormData.personalDocuments.idCardIssuingState = 'EX';

      // OBS: O preenchimento é obrigatório para o 4Bee para gerar a ficha de registro, hoje quando é estrangeiro e não tem RG usam preencher o número do RG com o número do RNE.
      updatedFormData.personalDocuments.idCard = updatedFormData.personalDocuments.rneCard?.toString();

      updatedFormData.personalDocuments.idCardIssuanceDate = undefined;
      updatedFormData.personalDocuments.idCardIssuingAuthority = undefined;
    }

    if (updatedFormData.personalDocuments?.foreigner === false) {
      //update rules
      enableField(newRules.personalDocuments.idCard, true);
      removeField(newRules.personalDocuments.rneCard);
      removeField(newRules.personalDocuments.rneCardIssuingAuthority);
      removeField(newRules.personalDocuments.rneCardIssuanceDate);
      removeField(newRules.personalDocuments.foreignerCountryArrivalDate);
      removeField(newRules.personalDocuments.foreignerRneCountry);
      removeField(newRules.personalDocuments.foreignerNationalityCode);
      removeField(newRules.personalDocuments.foreignerEntryCondition);
      removeField(newRules.personalDocuments.foreignerIsMarriedWithBrazilian);
      removeField(newRules.personalDocuments.foreignerHasChildrenWithBrazilian);


      //Handles specific idCard conditions so that other conditions are updated
      if (updatedFormData.personalDocuments.idCard === updatedFormData.personalDocuments.rneCard) {
        updatedFormData.personalDocuments.idCard = undefined;
      }

      if (updatedFormData.personalDocuments.idCardIssuingState === 'EX') {
        updatedFormData.personalDocuments.idCardIssuingState = undefined;
      }

      if (updatedFormData.personalDocuments.idCard) {
        enableField(newRules.personalDocuments.idCardIssuingState, true);
        enableField(newRules.personalDocuments.idCardIssuanceDate, true);
        enableField(newRules.personalDocuments.idCardIssuingAuthority, true);
      } else {
        disableField(newRules.personalDocuments.idCardIssuingState, false);
        disableField(newRules.personalDocuments.idCardIssuanceDate, false);
        disableField(newRules.personalDocuments.idCardIssuingAuthority, false);

        updatedFormData.personalDocuments.idCardIssuingState = undefined;
        updatedFormData.personalDocuments.idCardIssuanceDate = undefined;
        updatedFormData.personalDocuments.idCardIssuingAuthority = undefined;


      }

      updatedFormData.personalDocuments.rneCard = undefined;
      updatedFormData.personalDocuments.rneCardIssuingAuthority = undefined;
      updatedFormData.personalDocuments.rneCardIssuanceDate = undefined;
      updatedFormData.personalDocuments.foreignerCountryArrivalDate = undefined;
      updatedFormData.personalDocuments.foreignerRneCountry = undefined;
      updatedFormData.personalDocuments.foreignerNationalityCode = undefined;
      updatedFormData.personalDocuments.foreignerEntryCondition = undefined;
      updatedFormData.personalDocuments.foreignerIsMarriedWithBrazilian = undefined;
      updatedFormData.personalDocuments.foreignerHasChildrenWithBrazilian = undefined;
    }
  }

  if (updatedFormData.personal.data?.gender === 'M') {
    enableField(newRules.personalDocuments.militaryReserveNumber);

    if (updatedFormData.personalDocuments.militaryReserveNumber) {
      enableField(newRules.personalDocuments.militaryReserveSeries);
      enableField(newRules.personalDocuments.militaryReserveCategory);
      enableField(newRules.personalDocuments.militaryReserveState);
    } else {
      disableField(newRules.personalDocuments.militaryReserveSeries);
      disableField(newRules.personalDocuments.militaryReserveCategory);
      disableField(newRules.personalDocuments.militaryReserveState);

      updatedFormData.personalDocuments.militaryReserveSeries = undefined;
      updatedFormData.personalDocuments.militaryReserveCategory = undefined;
      updatedFormData.personalDocuments.militaryReserveState = undefined;
    }

  } else {
    removeField(newRules.personalDocuments.militaryReserveNumber);
    removeField(newRules.personalDocuments.militaryReserveSeries);
    removeField(newRules.personalDocuments.militaryReserveCategory);
    removeField(newRules.personalDocuments.militaryReserveState);

    updatedFormData.personalDocuments.militaryReserveNumber = undefined;
    updatedFormData.personalDocuments.militaryReserveSeries = undefined;
    updatedFormData.personalDocuments.militaryReserveCategory = undefined;
    updatedFormData.personalDocuments.militaryReserveState = undefined;
  }

  if (updatedFormData.personalDocuments.driverLicenseNumber) {
    enableField(newRules.personalDocuments.driverLicenseExpiryDate, true);
    enableField(newRules.personalDocuments.driverLicenseExpeditionDate, false);
    enableField(newRules.personalDocuments.driverLicenseState, true);
    enableField(newRules.personalDocuments.driverLicenseFirstExpeditionDate, false);
    enableField(newRules.personalDocuments.driverLicenseCategory, true);
  } else {
    disableField(newRules.personalDocuments.driverLicenseExpiryDate, false);
    disableField(newRules.personalDocuments.driverLicenseExpeditionDate, false);
    disableField(newRules.personalDocuments.driverLicenseState, false);
    disableField(newRules.personalDocuments.driverLicenseFirstExpeditionDate, false);
    disableField(newRules.personalDocuments.driverLicenseCategory, false);

    updatedFormData.personalDocuments.driverLicenseExpiryDate = undefined;
    updatedFormData.personalDocuments.driverLicenseExpeditionDate = undefined;
    updatedFormData.personalDocuments.driverLicenseState = undefined;
    updatedFormData.personalDocuments.driverLicenseFirstExpeditionDate = undefined;
    updatedFormData.personalDocuments.driverLicenseCategory = undefined;
  }

  if (updatedFormData.personalDocuments.ricNumber) {
    enableField(newRules.personalDocuments.ricIssuingAuthority, true);
    enableField(newRules.personalDocuments.ricExpeditionDate, false);
  } else {
    disableField(newRules.personalDocuments.ricIssuingAuthority, false);
    disableField(newRules.personalDocuments.ricExpeditionDate, false);
    updatedFormData.personalDocuments.ricIssuingAuthority = undefined;
    updatedFormData.personalDocuments.ricExpeditionDate = undefined;
  }

  //normalizes values for better comparison
  const first7CpfDigits = cpfNormalizers.getFirst7Digits(updatedFormData.personal.data?.cpf || '');
  const last4CpfDigits = cpfNormalizers.getLast4Digits(updatedFormData.personal.data?.cpf || '');
  const currentWorkCardNumber = removeNonNumericValues(updatedFormData.professionalDocuments.workCardNumber || '');
  const currentWorkSeriesNumber = removeNonNumericValues(updatedFormData.professionalDocuments.workCardSeries || '');

  if (updatedFormData.professionalDocuments.workCardType) {
    if (updatedFormData.professionalDocuments.workCardType === WorkCardTypes.DIGITAL) {

      if (first7CpfDigits &&
        (!currentWorkCardNumber ||
          !currentWorkCardNumber.startsWith(first7CpfDigits))) {
        updatedFormData.professionalDocuments.workCardNumber = first7CpfDigits;
      }

      if (last4CpfDigits &&
        (!currentWorkSeriesNumber ||
          !currentWorkSeriesNumber.startsWith(last4CpfDigits))) {
        updatedFormData.professionalDocuments.workCardSeries = last4CpfDigits;
      }

      enableField(newRules.professionalDocuments.workCardNumber, true);
      enableField(newRules.professionalDocuments.workCardSeries, true);
      enableField(newRules.professionalDocuments.workCardState, true);
      enableField(newRules.professionalDocuments.workCardIssuanceDate, false);

    }

    if (updatedFormData.professionalDocuments.workCardType === WorkCardTypes.PHYSICAL) {

      if (first7CpfDigits &&
        (currentWorkCardNumber &&
          currentWorkCardNumber.startsWith(first7CpfDigits))) {
        updatedFormData.professionalDocuments.workCardNumber = undefined;
      }

      if (last4CpfDigits &&
        (currentWorkSeriesNumber &&
          currentWorkSeriesNumber.startsWith(last4CpfDigits))) {
        updatedFormData.professionalDocuments.workCardSeries = undefined;
      }

      enableField(newRules.professionalDocuments.workCardNumber, true);

      if (updatedFormData.professionalDocuments.workCardNumber) {
        enableField(newRules.professionalDocuments.workCardSeries, true);
        enableField(newRules.professionalDocuments.workCardState, true);
        enableField(newRules.professionalDocuments.workCardIssuanceDate, false);
      } else {
        disableField(newRules.professionalDocuments.workCardSeries, true);
        disableField(newRules.professionalDocuments.workCardState, true);
        disableField(newRules.professionalDocuments.workCardIssuanceDate, false);

        updatedFormData.professionalDocuments.workCardSeries = undefined;
        updatedFormData.professionalDocuments.workCardState = undefined;
        updatedFormData.professionalDocuments.workCardIssuanceDate = undefined;

      }
    }
  } else {
    //handles form loading when workCardType is not set
    if (updatedFormData.professionalDocuments.workCardNumber) {
      if (currentWorkCardNumber.startsWith(first7CpfDigits)) {
        //digital type
        updatedFormData.professionalDocuments.workCardType = WorkCardTypes.DIGITAL;
      } else {
        //physical type
        updatedFormData.professionalDocuments.workCardType = WorkCardTypes.PHYSICAL;
      }
    }
  }

  if (updatedFormData.professionalDocuments.specialWorkClassNumber) {
    enableField(newRules.professionalDocuments.specialWorkClassEnrollment, true);
  } else {
    disableField(newRules.professionalDocuments.specialWorkClassEnrollment, false);
    updatedFormData.professionalDocuments.specialWorkClassEnrollment = undefined;
  }

  //Bank details
  // main payment method
  if (updatedFormData.banking.paymentMethod && [
    PaymentMethods.RELACAO_BANCARIA.toString(),
    PaymentMethods.RELACAO_BANCARIA_DEP_CTA_POUPANCA.toString(),
  ].includes(updatedFormData.banking.paymentMethod)) {
    // makes fields required
    enableField(newRules.banking.bank, true);
    enableField(newRules.banking.agency, true);
    enableField(newRules.banking.account, true);
  }else{
    //Remove Fields and clear values
    removeField(newRules.banking.bank);
    removeField(newRules.banking.agency);
    removeField(newRules.banking.account);

    updatedFormData.banking.bank = undefined;
    updatedFormData.banking.agency = undefined;
    updatedFormData.banking.account = undefined;
  }

  // complementary payment method
  if (admissionMetadata.professionalIdentification === ProfessionalIdentificationTypes.ESTRATEGICO) {
    enableField(newRules.banking.bankComplementaryRemuneration, false);
    enableField(newRules.banking.complementaryAgency, false);
    enableField(newRules.banking.complementaryAccount, false);

    if (updatedFormData.banking.paymentMethodComplementaryRemuneration && [
      PaymentMethods.RELACAO_BANCARIA.toString(),
      PaymentMethods.RELACAO_BANCARIA_DEP_CTA_POUPANCA.toString(),
    ].includes(updatedFormData.banking.paymentMethodComplementaryRemuneration)) {
      // makes fields required
      enableField(newRules.banking.bankComplementaryRemuneration, true);
      enableField(newRules.banking.complementaryAgency, true);
      enableField(newRules.banking.complementaryAccount, true);
    }else{
      //Remove Fields and clear values
      removeField(newRules.banking.bankComplementaryRemuneration);
      removeField(newRules.banking.complementaryAgency);
      removeField(newRules.banking.complementaryAccount);

      updatedFormData.banking.bankComplementaryRemuneration = undefined;
      updatedFormData.banking.complementaryAgency = undefined;
      updatedFormData.banking.complementaryAccount = undefined;
    }
  } else {
    removeField(newRules.banking.paymentMethodComplementaryRemuneration);
    removeField(newRules.banking.bankComplementaryRemuneration);
    removeField(newRules.banking.complementaryAgency);
    removeField(newRules.banking.complementaryAccount);

    //clear field values if set
    updatedFormData.banking.paymentMethodComplementaryRemuneration = undefined;
    updatedFormData.banking.bankComplementaryRemuneration = undefined;
    updatedFormData.banking.complementaryAgency = undefined;
    updatedFormData.banking.complementaryAccount = undefined;
  }



  return { updatedFormData: updatedFormData, updatedFormRules: newRules };
};
const triggerFormRuleUpdateEvent = () => {
  window.dispatchEvent(new CustomEvent(Events.FORM_RULES.UPDATE));
};
const events = { triggerFormRuleUpdateEvent };
export { initialRules, updateRules, events };
